"use client";

import { useMemo, useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { useDebounce } from "use-debounce";
import { FadeIn } from "@/app/components/FadeIn";
import type { Product } from "@/app/lib/marketplace/orders";

const PLACEHOLDER_IMAGE =
  "https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?q=80&w=2564&auto=format&fit=crop";

const getCategoryLabel = (product: Product) => {
  const fallback = "Automation Tool";
  const metadata = product.metadata;

  if (!metadata) return fallback;

  if (typeof metadata === "string") {
    try {
      const parsed = JSON.parse(metadata) as { category?: unknown };
      if (typeof parsed?.category === "string" && parsed.category.trim()) {
        return parsed.category;
      }
    } catch {
      return fallback;
    }
  }

  if (typeof metadata === "object") {
    const category = (metadata as { category?: unknown }).category;
    if (typeof category === "string" && category.trim()) {
      return category;
    }
  }

  return fallback;
};

const formatCurrency = (amountCents: number, currency: string) => {
  const amount = amountCents / 100;
  const code = (currency || "USD").toUpperCase();

  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: code,
    }).format(amount);
  } catch {
    return `$${amount.toFixed(2)}`;
  }
};

export default function MarketContent({ products }: { products: Product[] }) {
  const [query, setQuery] = useState("");
  const [debouncedQuery] = useDebounce(query, 200);

  const filteredProducts = useMemo(() => {
    if (!Array.isArray(products)) return [];

    const normalizedQuery = debouncedQuery.trim().toLowerCase();
    if (!normalizedQuery) return products;

    return products.filter((product) => {
      if (!product) return false;

      const categoryLabel = getCategoryLabel(product);
      const haystack = [
        product.title,
        product.description,
        product.slug,
        product.sku,
        categoryLabel,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return haystack.includes(normalizedQuery);
    });
  }, [products, debouncedQuery]);

  return (
    <>
      <section className="max-w-[1440px] mx-auto px-margin-mobile md:px-margin-desktop mb-12">
        <FadeIn>
          <div className="flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
            <div className="space-y-3">
              <p className="font-label-sm text-label-sm uppercase tracking-[0.25em] text-secondary">
                Browse Tools
              </p>
              <h2 className="font-h2 text-h2 text-primary">
                Search the automation catalog
              </h2>
              <p className="font-body-md text-body-md text-secondary max-w-2xl">
                Find studio-made utilities for scraping, workflows, reporting,
                and data operations.
              </p>
            </div>
            <div className="w-full max-w-xl space-y-3">
              <div className="relative group">
                <span className="material-symbols-outlined absolute left-4 top-1/2 -translate-y-1/2 text-[20px] text-secondary group-focus-within:text-primary transition-colors">
                  search
                </span>
                <input
                  aria-label="Search automation tools"
                  className="w-full bg-transparent border border-outline-variant focus:border-primary focus:ring-1 focus:ring-primary pl-12 pr-20 py-3 font-label-sm text-label-sm uppercase tracking-widest text-primary placeholder:text-secondary/50 transition-all rounded-none"
                  placeholder="SEARCH TOOLS, CATEGORIES, OR USE CASES"
                  type="search"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                />
                {query ? (
                  <button
                    type="button"
                    onClick={() => setQuery("")}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] uppercase tracking-[0.2em] text-secondary hover:text-primary transition-colors"
                  >
                    Clear
                  </button>
                ) : null}
              </div>
              <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.2em] text-secondary">
                <span>{filteredProducts.length} tools available</span>
                <span>Commercial license included</span>
              </div>
            </div>
          </div>
        </FadeIn>
      </section>

      <section className="max-w-[1440px] mx-auto px-margin-mobile md:px-margin-desktop">
        {filteredProducts.length === 0 ? (
          <div className="border border-surface-variant bg-surface-lowest p-12 text-center text-secondary w-full">
            No tools match your search yet. Try a different keyword.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-gutter">
            {filteredProducts.map((product, index) => {
              const slug = product.slug || product.sku || product.id;
              const categoryLabel = getCategoryLabel(product);
              const priceLabel = formatCurrency(product.price_cents, product.currency);
              const comparePriceLabel = product.compare_at_price_cents
                ? formatCurrency(product.compare_at_price_cents, product.currency)
                : null;
              const description =
                product.description ||
                "Studio-made automation tool for modern teams.";

              return (
                <FadeIn key={product.id} delay={index * 0.08}>
                  <Link
                    className="group block h-full border border-outline-variant bg-surface hover:border-primary transition-all duration-300 rounded-2xl overflow-hidden"
                    href={`/market/${slug}`}
                  >
                    <article className="flex flex-col h-full">
                      <div className="relative w-full aspect-[16/9] border-b border-outline-variant overflow-hidden">
                        <Image
                          alt={product.title || "Automation tool"}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          src={product.thumbnail_url || PLACEHOLDER_IMAGE}
                          width={800}
                          height={450}
                          sizes="(max-width: 640px) 100vw, (max-width: 1200px) 50vw, 800px"
                        />
                      </div>
                      <div className="p-7 flex flex-col gap-4 flex-1">
                        <div className="flex items-start justify-between gap-4">
                          <span className="inline-flex items-center bg-surface-container-low text-primary font-label-sm text-label-sm px-2 py-1 rounded tracking-widest uppercase">
                            {categoryLabel}
                          </span>
                          <div className="text-right">
                            {comparePriceLabel ? (
                              <span className="block text-xs text-secondary line-through">
                                {comparePriceLabel}
                              </span>
                            ) : null}
                            <span className="block text-sm font-semibold text-primary">
                              {priceLabel}
                            </span>
                          </div>
                        </div>
                        <h3 className="font-h3 text-h3 text-primary">
                          {product.title}
                        </h3>
                        <p className="font-body-md text-body-md text-secondary">
                          {description}
                        </p>
                        <div className="mt-auto flex items-center justify-between text-[10px] uppercase tracking-[0.2em] text-secondary">
                          <span>Studio Made</span>
                          <span className="text-primary">View Tool</span>
                        </div>
                      </div>
                    </article>
                  </Link>
                </FadeIn>
              );
            })}
          </div>
        )}
      </section>
    </>
  );
}
